import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import SelectCustomOptionsField from 'components/SelectCustomOptionsField';
import useOperationStepValues from 'router/data/useOperationStepValues';
import {
  DEAL_CONTRACT_STATUS,
  DEAL_OFFER_STATUS,
  DEAL_STAGE,
  DealRoom,
  DealStage,
} from 'models/DealRoom';
import utils, {
  addDollarPreffix,
  checkHasListing,
  checkIsPaused,
  extractValueFromMoneyString,
} from 'utils';
import { DOLLAR } from 'data/global';
import RadioGroupField from 'components/RadioGroupField';
import { LISTING_STAGE } from 'models/Listing';
import { ChangeDealStage } from 'api/portfolio/seller';
import validations from 'validations';
import DatepickerField from 'components/DatepickerField';
import InputField from 'components/InputField';
import TextAreaField from 'components/TextAreaField';
import LeadsField from './components/LeadsField';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ListingContext from '../../providers/Listing/Listing.context';

import './ChangeDealStatusDrawer.styles.scss';

const MAX_LENGHT = 2000;

const LISTING_STAGE_OPTIONS = [
  LISTING_STAGE.PRIVATE,
  LISTING_STAGE.PUBLIC,
  LISTING_STAGE.PAUSED,
];

const DONT_SHOW_LISTING_STAGE_OPTIONS: DealStage[] = [
  DEAL_STAGE.SOLD,
  DEAL_STAGE.COMPLETE,
  DEAL_STAGE.LOST,
  DEAL_STAGE.ARCHIVED,
];

const getShouldShowListingChangeOption = (
  hasListing: boolean,
  dealStage: DealStage,
) => {
  return hasListing && !DONT_SHOW_LISTING_STAGE_OPTIONS.includes(dealStage);
};

type ChangeDealStatusDrawerProps = {
  className?: string;
  deal: DealRoom;
  updateDeal: (deal: DealRoom) => void;
};

const ChangeDealStatusDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ChangeDealStatusDrawerProps
> = (props, ref) => {
  const { className, deal, updateDeal } = props;

  const {
    stage,
    dealOutcome,
    listingStage,
    offerStatus,
    contractStatus,
    underOfferLeadId,
    isExternalUnderOfferLead,
    settlementDate,
    settlementPrice,
    lostMandateDescription,
  } = deal || {};

  const { popServerError } = useContext(PopNotificationsContext);
  const { leads } = useContext(ListingContext);

  const classes = classNames('change-deal-status-drawer', className);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const { getDealStageOptions } = useOperationStepValues();

  const dealStageOptions = getDealStageOptions(dealOutcome);

  const hasListing =
    checkHasListing(listingStage) || checkIsPaused(listingStage);

  const initialValues = useMemo(
    () => ({
      stage,
      offerStatus,
      contractStatus,
      underOfferLeadId,
      isExternalUnderOfferLead,
      settlementDate,
      lostMandateDescription,
      settlementPrice: utils.getDotedNumber(settlementPrice),
      listingStage: hasListing ? listingStage : undefined,
    }),
    [
      stage,
      offerStatus,
      contractStatus,
      underOfferLeadId,
      isExternalUnderOfferLead,
      settlementDate,
      lostMandateDescription,
      listingStage,
      settlementPrice,
      hasListing,
    ],
  );

  const listingStageOptions = useMemo(
    () =>
      LISTING_STAGE_OPTIONS.map((option) => ({
        value: option,
        label: t(option === listingStage ? 'keepIt' : 'makeIt', {
          type: option,
        }),
      })),
    [listingStage, t],
  );

  const offerStatusOptions = useMemo(
    () =>
      Object.values(DEAL_OFFER_STATUS).map((option) => ({
        value: option,
        label: t(option),
      })),
    [t],
  );

  const contractStatusOptions = useMemo(
    () =>
      Object.values(DEAL_CONTRACT_STATUS).map((option) => ({
        value: option,
        label: t(option),
      })),
    [t],
  );

  const onSubmit = useCallback(
    async (values: ChangeDealStage) => {
      const {
        stage,
        listingStage,
        offerStatus,
        contractStatus,
        underOfferLeadId,
        isExternalUnderOfferLead,
        settlementDate,
        settlementPrice,
        lostMandateDescription,
      } = values;

      let finalValues;

      if (stage === DEAL_STAGE.ARCHIVED) {
        finalValues = { stage };
      }

      if (stage === DEAL_STAGE.LOST) {
        finalValues = { stage, lostMandateDescription };
      }

      if (stage === DEAL_STAGE.SOLD || stage === DEAL_STAGE.COMPLETE) {
        finalValues = {
          stage,
          settlementDate,
          settlementPrice: utils.getIntegerFromDottedNumber(
            settlementPrice.toString(),
          ),
        };
      }

      if (stage === DEAL_STAGE.UNDER_OFFER) {
        finalValues = {
          stage,
          listingStage,
          offerStatus,
          isExternalUnderOfferLead,
          underOfferLeadId,
        };
      }

      if (stage === DEAL_STAGE.UNDER_CONTRACT) {
        finalValues = {
          stage,
          listingStage,
          contractStatus,
          isExternalUnderOfferLead,
          underOfferLeadId,
        };
      }

      if (
        (
          [
            DEAL_STAGE.SELLER_LEAD,
            DEAL_STAGE.MANDATED,
            DEAL_STAGE.LIVE,
          ] as DealStage[]
        ).includes(stage)
      ) {
        finalValues = { stage, listingStage };
      }

      try {
        const { data } = await api.portfolio.seller.changeDealStage(
          deal._id,
          finalValues,
        );
        updateDeal(data);
        drawer.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [deal, drawer, popServerError, updateDeal],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      title={t('updateDealStage')}
      className={classes}
    >
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({
          initialValues,
          values,
          submitting,
          pristine,
          handleSubmit,
        }) => {
          const hasStageChanged = initialValues.stage !== values.stage;

          const showListingOption =
            hasStageChanged &&
            getShouldShowListingChangeOption(hasListing, values.stage);

          return (
            <form onSubmit={handleSubmit}>
              <Field
                name="stage"
                label={t('dealStatus')}
                options={dealStageOptions}
                component={SelectCustomOptionsField}
                renderAsPortal
                portalClassName="stage-select"
              />

              {values.stage === DEAL_STAGE.UNDER_OFFER && (
                <RadioGroupField
                  name="offerStatus"
                  options={offerStatusOptions}
                  label={t('offerStatus')}
                />
              )}

              {values.stage === DEAL_STAGE.UNDER_CONTRACT && (
                <RadioGroupField
                  name="contractStatus"
                  options={contractStatusOptions}
                  label={t('contractStatus')}
                />
              )}

              {(values.stage === DEAL_STAGE.UNDER_OFFER ||
                values.stage === DEAL_STAGE.UNDER_CONTRACT) &&
                !!leads.length && (
                  <>
                    <p className="select-lead">
                      {t('selectWhichResolveLeadMadeTheOffer')}
                    </p>
                    <LeadsField leads={leads} />
                  </>
                )}

              {(values.stage === DEAL_STAGE.SOLD ||
                values.stage === DEAL_STAGE.COMPLETE) && (
                <>
                  <Field
                    label={t('settlementDate')}
                    name="settlementDate"
                    component={DatepickerField}
                  />
                  <Field
                    label={t('settlementPrice')}
                    name="settlementPrice"
                    placeholder={DOLLAR}
                    format={addDollarPreffix}
                    parse={extractValueFromMoneyString}
                    component={InputField}
                    validate={validations.validDecimalNumbersOnlyValidation(
                      t('numbersOnlyError'),
                    )}
                  />
                </>
              )}

              {values.stage === DEAL_STAGE.LOST && (
                <Field
                  name="lostMandateDescription"
                  label={t('addNoteAboutMandate')}
                  maxLength={MAX_LENGHT}
                  component={TextAreaField}
                />
              )}

              {showListingOption && (
                <RadioGroupField
                  name="listingStage"
                  options={listingStageOptions}
                  label={t('wouldYouLikeToMakeTheListing')}
                />
              )}

              <div className="actions">
                <Button variant="link" onClick={() => drawer.setOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={submitting || pristine}>
                  {t('confirmStatusChange')}
                </Button>
              </div>
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ChangeDealStatusDrawer);
